import React, { useState } from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

const PAGE_SIZE = 5;

function ResultGroup({ group, formatFields, onInspect, busy }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const records = Array.isArray(group.result) ? group.result : [];
  const blue = dark ? "#99c9ff" : "#075bb8";
  const ink = dark ? "#ffffff" : "#000000";
  const border = dark ? "#3b4c69" : "#dbe6f3";
  if (group.error) return <Typography role="alert" sx={{ color: dark ? "#ffb4b4" : "#a52b32", fontSize: 13 }}>These results could not be loaded. Please try again.</Typography>;
  return <Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {records.slice(0, visibleCount).map((record, index) => {
        const id = record.CustomerInvoiceReferenceID || record.SourceInvoiceReferenceID;
        const canInspect = typeof id === "string" && /^[a-z0-9][a-z0-9_./-]{0,119}$/i.test(id);
        const status = record.Status || record.WorkdayCustomerInvoiceStatus;
        const statusText = String(status || "");
        const statusColor = /fail|fault|error|reject/i.test(statusText)
          ? (dark ? "#ffb4b4" : "#a52b32")
          : /processed|approved|complete|paid/i.test(statusText)
            ? (dark ? "#8ee6bb" : "#086b46")
            : (dark ? "#b9d9ff" : "#195a9b");
        const fields = formatFields(record).filter(({ key }) =>
          !(id && ["CustomerInvoiceReferenceID", "SourceInvoiceReferenceID"].includes(key) && record[key] === id) &&
          !(status && ["Status", "WorkdayCustomerInvoiceStatus"].includes(key))
        );
        return <Box key={index} component="article" aria-label={id ? `Invoice ${id}` : `Result ${index + 1}`} sx={{
          p: 1.5, border: `1px solid ${border}`, borderRadius: "14px",
          bgcolor: dark ? "#192941" : "#ffffff", color: ink,
          minWidth: 0,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: id ? 0.75 : 1.25 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", color: blue, textTransform: "uppercase" }}>
              {id ? "Invoice" : record.CustomerInvoiceLineOrder != null ? `Line ${record.CustomerInvoiceLineOrder}` : "Details"}
            </Typography>
            {status && <Chip size="small" label={statusText} sx={{
              height: "auto", minHeight: 23, maxWidth: "70%", color: statusColor,
              bgcolor: dark ? "#293d54" : "#eef5fb", borderRadius: "6px", fontWeight: 700,
              "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", px: 0.9, py: 0.35, fontSize: 10 },
            }} />}
          </Box>
          {id && <Typography sx={{ color: ink, fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace", fontSize: 12, fontWeight: 600, lineHeight: 1.65, overflowWrap: "anywhere", mb: fields.length ? 1.25 : 0 }}>{String(id)}</Typography>}
          <Box component="dl" sx={{ m: 0, display: "grid", gap: 0.9 }}>
            {fields.map(({ key, label, display }) => <Box key={key} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr)", gap: 1.5, alignItems: "baseline" }}>
              <Typography component="dt" sx={{ fontSize: 11, color: blue, overflowWrap: "anywhere" }}>{label}</Typography>
              <Typography component="dd" sx={{ m: 0, color: ink, fontSize: 12, fontWeight: /amount|total/i.test(key) ? 700 : 500, textAlign: "right", overflowWrap: "anywhere", lineHeight: 1.5 }}>{String(display)}</Typography>
            </Box>)}
          </Box>
          {canInspect && <Button
            aria-label={`View invoice ${id}`} disabled={busy}
            onClick={() => onInspect(`Find invoice ID ${id}`)} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              mt: 1.25, px: 0, py: 0.5, minHeight: 32, color: blue, textTransform: "none", fontSize: 12, fontWeight: 700,
              "&:hover": { bgcolor: dark ? "#253c59" : "#edf5ff" },
              "&.Mui-disabled": { color: blue, opacity: 1 },
              "&.Mui-focusVisible": { outline: `2px solid ${blue}`, outlineOffset: 3 },
            }}
          >View invoice</Button>}
        </Box>;
      })}
    </Box>
    {records.length > PAGE_SIZE && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
      <Typography sx={{ fontSize: 11, color: blue }}>{Math.min(visibleCount, records.length)} of {records.length} shown</Typography>
      <Button size="small" onClick={() => setVisibleCount(visibleCount < records.length ? visibleCount + PAGE_SIZE : PAGE_SIZE)} endIcon={<ExpandMoreRoundedIcon sx={{ transform: visibleCount >= records.length ? "rotate(180deg)" : undefined }} />} sx={{ color: blue, textTransform: "none", fontSize: 12, fontWeight: 600 }}>
        {visibleCount < records.length ? "Show more" : "Show less"}
      </Button>
    </Box>}
  </Box>;
}

export default function ChatResults({ title, description, groups, formatFields, onInspect, busy }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const blue = dark ? "#99c9ff" : "#075bb8";
  const ink = dark ? "#ffffff" : "#000000";
  const total = groups.reduce((sum, group) => sum + (group.result?.length || 0), 0);
  return <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.25 }}>
      <Box sx={{ display: "flex", p: 0.75, borderRadius: "9px", bgcolor: dark ? "#253c59" : "#eaf3ff", color: dark ? "#99c9ff" : "#075bb8", flexShrink: 0 }}><ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} /></Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: blue, fontSize: 14, fontWeight: 700, lineHeight: 1.5, overflowWrap: "anywhere" }}>{title || "Search results"}</Typography>
        <Typography sx={{ fontSize: 11, color: blue, mt: 0.25 }}>{total} {total === 1 ? "record" : "records"} returned</Typography>
      </Box>
    </Box>
    {description && <Typography sx={{ fontSize: 12, lineHeight: 1.7, color: ink, mb: 1.5 }}>{description}</Typography>}
    {total === 0 && !description && !groups.some((group) => group.error) && <Typography sx={{ color: ink, fontSize: 13 }}>No matching records found.</Typography>}
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {groups.map((group, index) => <Box key={index}>
        {groups.length > 1 && <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 1, color: blue }}>{group.collection}</Typography>}
        <ResultGroup group={group} formatFields={formatFields} onInspect={onInspect} busy={busy} />
      </Box>)}
    </Box>
  </Box>;
}
